import getSymbolFromCurrency from "currency-symbol-map";
import { Copy } from "lucide-react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { LineItem } from "@/apis/gql/types";
import { Button } from "@/components/ui/button";
import { EVENT_NAMES } from "@/lib/events/names";
import { calculateCheckSums } from "@/lib/utils";

import { useUpdateInvoice } from "@/queries/invoices/update";
import Decimal from "decimal.js";
import { useInvoiceReviewContext } from "../../ReviewProvider";
import { InvoiceModes } from "../SplitInvoiceForm";

export interface TotalAmountsOverviewProps {
  totalAmount: number;
  lineItems: LineItem[];
  isNettoInvoice: boolean;
  currency: string;
  invoiceMode: InvoiceModes;
}

export function TotalAmountsOverview({
  totalAmount,
  lineItems,
  isNettoInvoice,
  currency,
  invoiceMode,
}: TotalAmountsOverviewProps) {
  const { t } = useTranslation();
  const { invoiceReviewState } = useInvoiceReviewContext();
  const { invoiceData } = invoiceReviewState;
  const [updateInvoice, { loading: updatingMode }] = useUpdateInvoice({
    config: { toasts: { success: false, loading: false }, refetchQueries: [] },
  });

  const {
    isCorrectAmount,
    bruttoSum,
    nettoSum,
    nettoSums,
    displayedBruttoSum,
  } = useMemo(
    () => calculateCheckSums(totalAmount, lineItems, isNettoInvoice),
    [totalAmount, lineItems, isNettoInvoice],
  );

  const errorValue = useMemo(() => {
    if (totalAmount) {
      return totalAmount - displayedBruttoSum;
    }

    return 0;
  }, [totalAmount, displayedBruttoSum]);

  const errorAmountMessage = () => {
    if (totalAmount) {
      const error = errorValue;
      if (error > 0) {
        return `${getSymbolFromCurrency(currency ?? "")}${error.toLocaleString(
          "de-DE",
          {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          },
        )} `;
      }
      if (error < 0) {
        return `${getSymbolFromCurrency(currency ?? "")}${Math.abs(
          error,
        ).toLocaleString("de-DE", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      }
    }

    return "";
  };

  const handleRestPropagation = (value: number) => {
    const roundedValue = new Decimal(value).toDecimalPlaces(2).toNumber();
    const event = new CustomEvent(EVENT_NAMES.PROPAGATE_AMOUNT, {
      detail: { value: roundedValue },
    });
    window.dispatchEvent(event);
  };

  const handleCopyButtonClick = async () => {
    try {
      if (invoiceMode === InvoiceModes.Netto && invoiceData?.invoiceId) {
        await updateInvoice({
          invoiceId: invoiceData.invoiceId,
          isNettoInvoice: false,
        });
      }
    } catch (e) {
      console.error(
        "Failed to auto-update invoice to brutto mode after copy remainder button click:",
        e,
      );
    } finally {
      handleRestPropagation(errorValue);
    }
  };

  return (
    <div className="flex flex-row bg-gray-100 rounded-md px-6 py-3">
      <div className="flex-1">
        <div className="flex flex-col gap-2">
          {nettoSums && nettoSums.size > 1 && (
            <div className="text-left font-bold text-[0.875rem]">Gesamt</div>
          )}
          <div className="flex flex-col gap-1">
            <div className="flex items-center">
              <div className="w-[50px] text-left text-sm">
                {t("netto_total")}
              </div>
              <div className="w-[120px] text-right text-[0.875rem] pr-2">
                {getSymbolFromCurrency(currency ?? "")}
                {nettoSum.toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div className="w-[200px]"></div>
            </div>
            <div className="flex items-center">
              <div className="w-[50px] text-left text-sm font-bold">
                {t("brutto_total")}
              </div>
              <div
                className={`w-[120px] text-right text-[0.875rem] font-bold pr-2 ${
                  isCorrectAmount ? "text-[#259834]" : "text-black"
                }`}
              >
                {getSymbolFromCurrency(currency ?? "")}
                {displayedBruttoSum.toLocaleString("de-DE", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
              <div className="w-[200px]"></div>
            </div>
            {!isCorrectAmount && (
              <div className="flex items-center">
                <div className="w-[50px] text-left text-red-500 text-[0.875rem] font-bold whitespace-nowrap">
                  {errorValue > 0 ? "Zu wenig:" : "Zu viel:"}
                </div>
                <div className="w-[120px] text-right text-red-500 text-[0.875rem] font-bold whitespace-nowrap pr-2">
                  {errorAmountMessage()}
                </div>
                <div className="w-[200px]">
                  <Button
                    onClick={handleCopyButtonClick}
                    variant="outline"
                    size="sm"
                    disabled={updatingMode}
                    className="text-[0.875rem] font-normal whitespace-nowrap px-1 py-0 h-5 leading-none"
                  >
                    <Copy size={14} className="mr-1" />
                    {t("copy_amounts")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col gap-2">
        <div className="flex flex-col gap-2 max-w-[200px]">
          {nettoSums && nettoSums.size > 1 && (
            <div className="text-left font-bold text-[0.875rem] whitespace-nowrap w-[200px]">
              Warenwert nach USt (Netto)
            </div>
          )}
          {nettoSums &&
            nettoSums.size > 1 &&
            Array.from(nettoSums.entries()).map(([rate, sum]) => (
              <div key={rate} className="flex flex-col gap-1">
                <div className="flex flex-row items-center gap-2">
                  <div className="text-left text-sm">{`${rate}%:`}</div>
                  <div className="text-right text-[0.875rem] whitespace-nowrap">
                    {getSymbolFromCurrency(currency ?? "")}
                    {sum.toNumber().toLocaleString("de-DE", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
